import React from 'react';

/**
 * Overlay shown on top of existing list/card data while a page or filter refresh loads.
 */
export const DataViewLoadingOverlay = ({
  show,
  message = 'Loading…',
}) => {
  if (!show) return null;

  return (
    <div
      className="absolute inset-0 z-20 flex items-center justify-center bg-white/70 backdrop-blur-[1px]"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="flex items-center gap-2.5 rounded-lg border border-indigo-100 bg-white px-4 py-2.5 shadow-md">
        <svg
          className="h-4 w-4 animate-spin text-indigo-600"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <span className="text-sm font-medium text-gray-700">{message}</span>
      </div>
    </div>
  );
};

/**
 * Skeleton rows for table/list view initial loads.
 */
export const TableSkeleton = ({
  columns = 5,
  rows = 8,
  columnWidths = null,
}) => {
  const widths = columnWidths || Array.from({ length: columns }, () => 'w-24');

  return (
    <div className="overflow-x-auto max-h-[70vh]" aria-busy="true" aria-label="Loading table">
      <table className="min-w-max w-full divide-y divide-gray-200">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr>
            {Array.from({ length: columns }).map((_, index) => (
              <th
                key={`sk-h-${index}`}
                className={`px-4 py-3 ${index === columns - 1 ? 'text-right' : 'text-left'}`}
              >
                <div className={`h-3 ${widths[index] || 'w-20'} rounded bg-gray-200 animate-pulse ${index === columns - 1 ? 'ml-auto' : ''}`} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={`sk-r-${rowIndex}`} className="animate-pulse">
              {Array.from({ length: columns }).map((__, colIndex) => (
                <td key={`sk-c-${rowIndex}-${colIndex}`} className="px-4 py-3">
                  <div
                    className={`h-3 rounded bg-gray-100 ${
                      colIndex === 0 ? 'w-40' : widths[colIndex] || 'w-20'
                    } ${colIndex === columns - 1 ? 'ml-auto' : ''}`}
                  />
                  {colIndex === 0 && (
                    <div className="mt-1.5 h-2.5 w-28 rounded bg-gray-50" />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Skeleton cards for card-view initial loads.
 */
export const CardGridSkeleton = ({ count = 8 }) => (
  <div
    className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4"
    aria-busy="true"
    aria-label="Loading cards"
  >
    {Array.from({ length: count }).map((_, index) => (
      <div
        key={`sk-card-${index}`}
        className="h-28 rounded border border-gray-200 bg-white p-3 animate-pulse"
      >
        <div className="flex items-start gap-2">
          <div className="h-6 w-6 rounded-full bg-gray-200" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="h-3 w-2/3 rounded bg-gray-200" />
            <div className="h-2.5 w-1/2 rounded bg-gray-100" />
          </div>
        </div>
        <div className="mt-4 space-y-2">
          <div className="h-2.5 w-full rounded bg-gray-100" />
          <div className="h-2.5 w-3/4 rounded bg-gray-50" />
        </div>
        <div className="mt-3 flex gap-1">
          <div className="h-5 w-14 rounded bg-gray-100" />
          <div className="h-5 w-12 rounded bg-gray-50" />
        </div>
      </div>
    ))}
  </div>
);

/**
 * Wrap list/card content with a relative container and optional refresh overlay.
 */
export const DataViewLoadingContainer = ({
  loading = false,
  children,
  message = 'Loading…',
  className = '',
}) => (
  <div className={`relative min-h-[12rem] ${className}`}>
    <div className={loading ? 'pointer-events-none select-none opacity-50' : ''}>
      {children}
    </div>
    <DataViewLoadingOverlay show={loading} message={message} />
  </div>
);
